import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatPrice, formatPropertyPrice, isShortStayContract } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Bed, Bath, Maximize, MapPin, Lock, Heart, Phone, Mail, ChevronLeft, ChevronRight, Building2, MessageCircle, Share2, Calendar as CalendarIcon, BadgeCheck, Play, View, X as XIcon, Eye, Users, Flame, Snowflake, Armchair, WashingMachine, UtensilsCrossed, MoveVertical, DoorOpen, Trees, CircleParking, FileText, ExternalLink, Images, LayoutTemplate } from "lucide-react";
import PropertyMap from "@/components/PropertyMap";
import ContactForm from "@/components/ContactForm";
import ShareModal from "@/components/ShareModal";
import BookVisit from "@/components/BookVisit";
import PhotoTour from "@/components/PhotoTour";
import MatchBadge from "@/components/MatchBadge";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import { computeMatchScore, readUserPrefs } from "@/lib/matchScore";
import { useSeo, propertySeoTitle, propertyPath, propertyJsonLd, cityPath } from "@/lib/seo";

const CONDITION_LABELS = {
    nuovo: "Nuovo", ristrutturato: "Ristrutturato", abitabile: "Abitabile", da_ristrutturare: "Da ristrutturare",
    // valori legacy pre-restyling — mappati sull'equivalente più vicino
    nuova_costruzione: "Nuovo", buono_stato: "Abitabile",
};
const HEATING_LABELS = { autonomo: "Autonomo", centralizzato: "Centralizzato" };
const GARDEN_LABELS = { privato: "Privato", condominiale: "Condominiale", no: "Nessuno" };
const PARKING_LABELS = { box: "Box", coperto: "Coperto", scoperto: "Scoperto", no: "Nessuno" };

// Sezione "Caratteristiche e Servizi" — solo le voci effettivamente compilate
// (nessuna riga "Non specificato", come richiesto).
const getFeatureItems = (p) => {
    const items = [];
    if (p.heating) items.push({ testid: "feature-heating", icon: Flame, label: "Riscaldamento", value: HEATING_LABELS[p.heating] || p.heating });
    if (p.air_conditioning != null) items.push({ testid: "feature-ac", icon: Snowflake, label: "Aria condizionata", value: p.air_conditioning ? "Sì" : "No" });
    if (p.furnished != null) items.push({ testid: "feature-furnished", icon: Armchair, label: "Arredamento", value: p.furnished ? "Sì" : "No" });
    if (p.washing_machine != null) items.push({ testid: "feature-washing-machine", icon: WashingMachine, label: "Lavatrice", value: p.washing_machine ? "Sì" : "No" });
    if (p.dishwasher != null) items.push({ testid: "feature-dishwasher", icon: UtensilsCrossed, label: "Lavastoviglie", value: p.dishwasher ? "Sì" : "No" });
    if (p.elevator != null) items.push({ testid: "feature-elevator", icon: MoveVertical, label: "Ascensore", value: p.elevator ? "Sì" : "No" });
    if (p.balcony != null) items.push({ testid: "feature-balcony", icon: DoorOpen, label: "Balcone/Terrazzo", value: p.balcony ? "Sì" : "No" });
    if (p.garden) items.push({ testid: "feature-garden", icon: Trees, label: "Giardino", value: GARDEN_LABELS[p.garden] || p.garden });
    if (p.parking) items.push({ testid: "feature-parking", icon: CircleParking, label: "Posto auto", value: PARKING_LABELS[p.parking] || p.parking });
    return items;
};

export default function PropertyDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activePhoto, setActivePhoto] = useState(0);
    const [favLoading, setFavLoading] = useState(false);
    const [showShare, setShowShare] = useState(false);
    const [showBookVisit, setShowBookVisit] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [showTourModal, setShowTourModal] = useState(false);
    const [showPhotoTour, setShowPhotoTour] = useState(false);
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    const [stats, setStats] = useState({ views_24h: 0, contacts_24h: 0 });
    const floorplanRef = useRef(null);
    const scrollToFloorplan = () => floorplanRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    useEffect(() => {
        setLoading(true);
        setActivePhoto(0);
        api.get(`/properties/${id}`).then((r) => {
            const p = r.data;
            setProperty(p);
            // Vecchi link /annunci/{id} → redirect automatico all'URL leggibile
            if (p.slug && p.slug !== id) navigate(`/annunci/${p.slug}`, { replace: true });
            api.post(`/properties/${p.id}/view`).catch(() => {});
            api.get(`/properties/${p.id}/stats`).then((s) => setStats(s.data)).catch(() => {});
        }).catch(() => setProperty(null)).finally(() => setLoading(false));
    }, [id, navigate]);

    useSeo({
        title: property ? propertySeoTitle(property) : "Annuncio immobiliare | VendiDirect",
        description: property ? `${property.title}. ${property.rooms} locali, ${property.surface_sqm} m² a ${property.city}${property.zone ? ` (${property.zone})` : ""}. ${formatPrice(property.price)}${property.transaction_type === "affitto" ? "/mese" : ""}. ${property.description || ""}` : undefined,
        canonical: property ? propertyPath(property) : undefined,
        image: property?.photos?.[0],
        type: "product",
        jsonLd: property ? propertyJsonLd(property) : null,
    }, [property]);

    const addFavorite = async () => {
        if (!user) { navigate("/login"); return; }
        setFavLoading(true);
        try { await api.post("/favorites", { property_id: id }); } finally { setFavLoading(false); }
    };

    if (loading) return <div className="pt-32 w-full px-6 md:px-12 text-ink-400">Caricamento...</div>;
    if (!property) return <div className="pt-32 w-full px-6 md:px-12 text-ink-400">Annuncio non trovato.</div>;

    const photos = property.photos?.length ? property.photos : ["https://images.unsplash.com/photo-1545597390-c92ae038a5a8?auto=format&fit=crop&w=1600&q=80"];
    const locked = property.locked;
    const goToPhoto = (direction) => setActivePhoto((current) => (current + direction + photos.length) % photos.length);
    const thumbnailIndices = photos.length > 1
        ? Array.from({ length: Math.min(3, photos.length - 1) }, (_, offset) => (activePhoto + offset + 1) % photos.length)
        : [];
    const remainingPhotos = Math.max(0, photos.length - 4);
    const photoLabel = (index) => {
        const metadata = property.photo_metadata?.[index] || property.photos_metadata?.[index];
        return metadata?.room_name || metadata?.label || metadata?.name || property.photo_labels?.[index] || "";
    };

    // YouTube / Vimeo URL → embed URL. Matterport/altri → mantieni URL originale (già embed-compatibile).
    const getEmbedUrl = (url) => {
        if (!url) return null;
        try {
            const u = new URL(url);
            // YouTube long form: youtube.com/watch?v=ID
            if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) {
                return `https://www.youtube.com/embed/${u.searchParams.get("v")}?autoplay=1&rel=0`;
            }
            // YouTube short form: youtu.be/ID
            if (u.hostname === "youtu.be") {
                const id = u.pathname.replace("/", "");
                if (id) return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
            }
            // YouTube already-embed form
            if (u.hostname.includes("youtube.com") && u.pathname.startsWith("/embed/")) return url;
            // Vimeo: vimeo.com/ID → player.vimeo.com/video/ID
            if (u.hostname.includes("vimeo.com") && !u.hostname.includes("player")) {
                const id = u.pathname.split("/").filter(Boolean)[0];
                if (id) return `https://player.vimeo.com/video/${id}?autoplay=1`;
            }
            return url;
        } catch { return url; }
    };
    const videoEmbedUrl = getEmbedUrl(property.video_url);
    const tourEmbedUrl = property.virtual_tour_url || null;
    const photoTourReady = !tourEmbedUrl && property.photo_tour_enabled && photos.length >= 3;
    const floorplanIsPdf = property.floorplan_content_type === "application/pdf" || /\.pdf(?:$|\?)/i.test(property.floorplan_url || "");
    const matchScore = computeMatchScore(readUserPrefs(user), property);

    return (
        <div data-testid="property-detail-page" className="property-detail-light pt-28 pb-24 min-h-screen">
            <div className="w-full px-6 md:px-12 lg:px-20 2xl:px-28">
                <Link to="/annunci" className="inline-flex items-center gap-2 text-sm text-ink-400 hover:text-gold transition-colors mb-6">
                    <ChevronLeft size={14} /> Torna agli annunci
                </Link>

                {/* Gallery */}
                <div data-testid="property-gallery" className={`grid grid-cols-1 ${thumbnailIndices.length > 0 ? "lg:grid-cols-[minmax(0,7fr)_minmax(15rem,3fr)]" : ""} gap-3 mb-6 max-w-6xl mx-auto`}>
                    <div className="aspect-[16/11] sm:aspect-[16/9] lg:aspect-auto lg:h-[27rem] overflow-hidden bg-slate-100 relative rounded-lg">
                        <img data-testid="property-main-photo" src={photos[activePhoto]} alt={`${property.title} - ${property.city}`} fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
                        {photos.length > 1 && (
                            <>
                                <button
                                    data-testid="property-gallery-previous"
                                    type="button"
                                    onClick={() => goToPhoto(-1)}
                                    aria-label="Foto precedente"
                                    className="property-gallery-control absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-black/55 border border-white/25 text-white hover:bg-black/75 transition-colors"
                                >
                                    <ChevronLeft size={24} />
                                </button>
                                <button
                                    data-testid="property-gallery-next"
                                    type="button"
                                    onClick={() => goToPhoto(1)}
                                    aria-label="Foto successiva"
                                    className="property-gallery-control absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-black/55 border border-white/25 text-white hover:bg-black/75 transition-colors"
                                >
                                    <ChevronRight size={24} />
                                </button>
                            </>
                        )}
                        <div className="absolute bottom-3 sm:bottom-4 right-3 sm:right-4 flex items-center gap-2">
                            {property.floorplan_url && (
                                <button
                                    type="button"
                                    data-testid="gallery-floorplan-shortcut"
                                    title="Planimetria"
                                    aria-label="Vai alla planimetria"
                                    onClick={scrollToFloorplan}
                                    className="property-gallery-control inline-flex items-center justify-center w-9 h-9 bg-black/65 border border-white/20 text-white hover:bg-black/80 transition-colors rounded-md"
                                >
                                    <LayoutTemplate size={16} />
                                </button>
                            )}
                            <div data-testid="property-gallery-count" className="property-gallery-control inline-flex items-center bg-black/65 border border-white/20 px-3 py-1.5 text-xs font-medium text-white rounded-md">
                                {activePhoto + 1}/{photos.length}
                            </div>
                        </div>
                        {matchScore != null && (
                            <div className="absolute top-4 right-4 z-10" data-testid="match-badge-detail">
                                <MatchBadge score={matchScore} size="lg" />
                            </div>
                        )}
                        {videoEmbedUrl && (
                            <button
                                data-testid="thumb-video-play"
                                type="button"
                                onClick={() => setShowVideoModal(true)}
                                className="property-gallery-control absolute top-4 left-4 inline-flex items-center gap-2 bg-black/65 border border-white/20 px-3 py-2 text-xs font-medium text-white hover:bg-black/80 transition-colors rounded-md"
                            >
                                <Play size={14} fill="currentColor" /> Video
                            </button>
                        )}
                    </div>
                    {thumbnailIndices.length > 0 && (
                    <div data-testid="property-gallery-thumbnails" className={`grid ${remainingPhotos > 0 ? "grid-cols-2 lg:grid-cols-1 lg:grid-rows-4" : "grid-cols-2 lg:grid-cols-1"} gap-3 lg:h-[27rem]`}>
                        {thumbnailIndices.map((photoIndex, slotIndex) => (
                            <button
                                key={`${photoIndex}-${slotIndex}`}
                                onClick={() => setActivePhoto(photoIndex)}
                                data-testid={`thumb-${slotIndex}`}
                                type="button"
                                aria-label={`Mostra foto ${photoIndex + 1}`}
                                className="property-gallery-thumbnail no-pill relative aspect-[4/3] lg:aspect-auto lg:min-h-0 overflow-hidden opacity-90 hover:opacity-100 focus-visible:ring-2 focus-visible:ring-gold transition-opacity duration-200 rounded-lg"
                            >
                                <img src={photos[photoIndex]} alt={`${property.title} foto ${photoIndex + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                {photoLabel(photoIndex) && (
                                    <span data-testid={`thumb-label-${slotIndex}`} className="property-gallery-control absolute left-2 bottom-2 max-w-[calc(100%-1rem)] bg-black/65 text-white text-[11px] px-2 py-1 rounded-md truncate">{photoLabel(photoIndex)}</span>
                                )}
                            </button>
                        ))}
                        {remainingPhotos > 0 && (
                            <button
                                data-testid="property-gallery-open-all"
                                type="button"
                                onClick={() => setShowGalleryModal(true)}
                                className="property-gallery-more no-pill relative aspect-[4/3] lg:aspect-auto lg:min-h-0 rounded-lg overflow-hidden text-white grid place-items-center text-lg font-semibold transition-opacity"
                            >
                                <img src={photos[(activePhoto + 4) % photos.length]} alt={`${property.title} altre foto`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                                <span className="absolute inset-0 bg-slate-950/70 grid place-items-center">
                                    <span className="inline-flex items-center gap-2"><Images size={19} /> +{remainingPhotos} foto</span>
                                </span>
                            </button>
                        )}
                    </div>
                    )}
                </div>

                {(tourEmbedUrl || photoTourReady) && (
                    <div className="mb-10">
                        {tourEmbedUrl ? (
                            <button
                                data-testid="virtual-tour-open-btn"
                                type="button"
                                onClick={() => setShowTourModal(true)}
                                className="w-full md:w-auto inline-flex items-center justify-center gap-3 px-6 md:px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-medium tracking-wide shadow-xl hover:shadow-blue-500/30 transition-all rounded-sm border border-white/10"
                            >
                                <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                                    <View size={18} />
                                </span>
                                <span className="text-sm md:text-base">Virtual Tour 3D</span>
                                <span className="text-[11px] uppercase tracking-widest text-white/70 hidden sm:inline">· Esplora ora</span>
                            </button>
                        ) : (
                            <button
                                data-testid="photo-tour-open-btn"
                                type="button"
                                onClick={() => setShowPhotoTour(true)}
                                className="w-full md:w-auto inline-flex items-center justify-center gap-3 px-6 md:px-8 py-4 bg-gradient-to-r from-gold to-gold-soft hover:from-gold-soft hover:to-gold text-black font-medium tracking-wide shadow-xl hover:shadow-gold/30 transition-all rounded-sm"
                            >
                                <span className="w-9 h-9 rounded-full bg-black/15 flex items-center justify-center">
                                    <Play size={18} fill="currentColor" className="ml-0.5" />
                                </span>
                                <span className="text-sm md:text-base">Tour dell&apos;immobile</span>
                                <span className="text-[11px] uppercase tracking-widest text-black/70 hidden sm:inline">· Foto animate</span>
                            </button>
                        )}
                    </div>
                )}

                {/* Banner LIVE FOMO — subito dopo hero, prima di Header. Mobile: primo elemento sotto foto. */}
                {(stats.views_24h > 0 || stats.contacts_24h > 0) && (
                    <div
                        data-testid="live-banner-top"
                        className="live-banner-top flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 px-5 sm:px-7 py-4 sm:py-5 mb-10 rounded-sm"
                    >
                        <div className="flex items-center gap-2 shrink-0">
                            <span className="live-banner-radar relative inline-flex items-center justify-center w-10 h-10">
                                <span className="live-banner-radar-ring absolute inset-0 rounded-full" aria-hidden="true" />
                                <Flame size={20} className="live-banner-icon relative z-[1]" />
                            </span>
                            <span className="live-banner-tag text-[10px] tracking-[0.35em] uppercase font-bold px-2 py-0.5 rounded-sm">DATO LIVE</span>
                        </div>
                        <div className="live-banner-copy flex-1 text-sm sm:text-base leading-relaxed">
                            <b className="live-banner-num">{stats.views_24h}</b> persone interessate stanno guardando questa casa.
                            <span className="mx-1 opacity-70 hidden sm:inline">·</span>
                            <br className="sm:hidden" />
                            <b className="live-banner-num">{stats.contacts_24h}</b> richieste di contatto ricevute nelle ultime 24 ore.
                        </div>
                    </div>
                )}

                {/* Header + Description + Locked Section (colonna unica per evitare spazi vuoti quando la sidebar e' piu' alta del contenuto) */}
                {(() => {
                    const shortStay = isShortStayContract(property);
                    const titleBlockContent = (
                        <div data-testid="property-title-block">
                            <div className="flex items-center gap-2 text-sm text-ink-400 mb-3">
                                <MapPin size={14} className="text-gold" />
                                <Link to={cityPath(property.city)} data-testid="detail-city-link" className="tracking-wider uppercase text-xs hover:text-gold transition-colors">{property.city}</Link>
                                {property.zone && <span data-testid="detail-zone" className="tracking-wider uppercase text-xs">· {property.zone}</span>}
                                <span className="tracking-wider uppercase text-xs">· {property.region}</span>
                            </div>
                            <h1 data-testid="property-title" className="font-serif font-bold text-4xl sm:text-5xl text-ink-50 leading-tight">{property.title}</h1>
                            {property.documentation_verified && (
                                <div data-testid="badge-docs-verified" className="inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 border border-green-700/40 bg-green-900/15 text-green-300 text-[11px] tracking-wider uppercase">
                                    <BadgeCheck size={13} /> Documentazione verificata
                                </div>
                            )}
                            <div className="flex flex-wrap gap-8 mt-8 text-ink-100">
                                <div className="flex items-center gap-2"><Bed size={18} className="text-gold" /> {property.rooms} locali</div>
                                <div className="flex items-center gap-2"><Bath size={18} className="text-gold" /> {property.bathrooms} bagni</div>
                                <div className="flex items-center gap-2"><Maximize size={18} className="text-gold" /> {property.surface_sqm} m²</div>
                                {property.floor && <div className="text-ink-400 text-sm">Piano: {property.floor}</div>}
                                {property.energy_class && <div className="text-ink-400 text-sm">Classe energetica: <span className="text-gold">{property.energy_class}</span></div>}
                            </div>
                            <div data-testid="property-meta-extra" className="flex flex-wrap gap-x-8 gap-y-2 mt-5 text-xs text-ink-400 tracking-wider uppercase">
                                {property.condition && (
                                    <div>Stato immobile: <span className="text-ink-100">{CONDITION_LABELS[property.condition] || property.condition}</span></div>
                                )}
                                {property.transaction_type === "affitto" && (
                                    <div>Contratto: <span className="text-ink-100">{property.contract_type ? {residenziale: "Residenziale", transitorio: "Transitorio", lungo_termine: "Lungo termine", casa_vacanze: "Casa vacanze", bb: "B&B", affitto_breve: "Affitto breve"}[property.contract_type] : "Non specificato"}</span></div>
                                )}
                            </div>
                        </div>
                    );
                    const descBlockContent = (
                        <div>
                            <span className="label-eyebrow">Descrizione</span>
                            <p data-testid="property-description" className="text-base sm:text-lg font-normal text-ink-100 leading-8 mt-3 max-w-4xl">{property.description}</p>
                        </div>
                    );
                    const featBlockContent = getFeatureItems(property).length > 0 ? (
                        <div data-testid="feature-section">
                            <span className="label-eyebrow">Caratteristiche e Servizi</span>
                            <div className="feature-card-neu grid grid-cols-2 sm:grid-cols-3 gap-6 p-8 mt-4">
                                {getFeatureItems(property).map((it) => (
                                    <div key={it.testid} data-testid={it.testid} className="flex items-center gap-3">
                                        <span className="feature-icon-badge"><it.icon size={16} /></span>
                                        <div className="min-w-0">
                                            <div className="text-[10px] text-ink-400 uppercase tracking-wider truncate">{it.label}</div>
                                            <div className="text-sm text-ink-50">{it.value}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null;
                    const featureTagsBlockContent = property.features?.length > 0 ? (
                        <div>
                            <span className="label-eyebrow">Caratteristiche</span>
                            <div className="flex flex-wrap gap-2 mt-4">
                                {property.features.map((f, i) => (
                                    <span key={i} className="px-4 py-2 border border-white/10 text-sm text-ink-100">{f}</span>
                                ))}
                            </div>
                        </div>
                    ) : null;
                    const planBlockContent = property.floorplan_url ? (
                        <section ref={floorplanRef} data-testid="public-floorplan-section" className="scroll-mt-28">
                            <span className="label-eyebrow">Planimetria</span>
                            <div className="feature-card-neu mt-4 p-4 sm:p-6">
                                {floorplanIsPdf ? (
                                    <div data-testid="public-floorplan-pdf" className="space-y-4">
                                        <div className="aspect-[4/3] md:aspect-[16/10] w-full overflow-hidden bg-white/95 border border-white/10">
                                            <iframe src={property.floorplan_url} title="Planimetria catastale" className="w-full h-full border-0" />
                                        </div>
                                        <a data-testid="public-floorplan-open-link" href={property.floorplan_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 border border-gold/50 text-gold hover:bg-gold hover:text-black transition-colors text-sm"><FileText size={16} /> Apri / Scarica planimetria <ExternalLink size={13} /></a>
                                    </div>
                                ) : (
                                    <div data-testid="public-floorplan-image" className="w-full bg-white/95 p-3 sm:p-6">
                                        <img src={property.floorplan_url} alt={`Planimetria di ${property.title}`} loading="lazy" className="w-full max-h-[760px] object-contain mx-auto" />
                                    </div>
                                )}
                            </div>
                        </section>
                    ) : null;
                    const priceBlockContent = (
                        <aside className="w-full">
                            <div className="border border-white/10 p-5 sm:p-6 bg-ink-900/50">
                                <div className="font-serif text-3xl text-price mb-2">{formatPropertyPrice(property)}</div>
                                <div className="text-xs tracking-[0.2em] uppercase text-ink-400 mb-6">Prezzo {property.transaction_type}</div>
                                {shortStay && property.booking_url && (
                                    <a
                                        href={property.booking_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="booking-now-button"
                                        className="w-full bg-gold text-black py-3 text-sm font-medium hover:bg-gold-soft transition-colors flex items-center justify-center gap-2 mb-3"
                                    >
                                        <ExternalLink size={14} /> Prenota ora
                                    </a>
                                )}
                                <button
                                    onClick={addFavorite}
                                    disabled={favLoading}
                                    data-testid="add-favorite-button"
                                    className="w-full border border-white/20 text-ink-50 py-3 text-sm hover:border-gold hover:text-gold transition-colors flex items-center justify-center gap-2 mb-3"
                                >
                                    <Heart size={14} /> Salva nei preferiti
                                </button>
                                {property.owner_id && (!user || user.user_id !== property.owner_id) && (
                                    <Link
                                        to={`/chat?to=${property.owner_id}&property=${property.id}&title=${encodeURIComponent(property.title)}`}
                                        data-testid="chat-with-owner"
                                        className="w-full bg-gold text-black py-3 text-sm font-medium hover:bg-gold-soft transition-colors flex items-center justify-center gap-2 mb-3"
                                    >
                                        <MessageCircle size={14} /> {property.owner_role === "agenzia" ? "Chatta con l'agenzia" : "Chatta col proprietario"}
                                    </Link>
                                )}
                                <button
                                    onClick={() => setShowShare(true)}
                                    data-testid="share-button"
                                    className="w-full border border-white/20 text-ink-50 py-3 text-sm hover:border-gold hover:text-gold transition-colors flex items-center justify-center gap-2"
                                >
                                    <Share2 size={14} /> Sponsorizza in modo anonimo
                                </button>
                            </div>
                        </aside>
                    );
                    const availBlockContent = shortStay && property.ical_url ? (
                        <div data-testid="availability-section" className="border border-gold/25 bg-gradient-to-br from-ink-900/80 to-ink-900/30 p-4">
                            <div className="flex items-center gap-3">
                                <span className="w-11 h-11 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                                    <CalendarIcon size={19} className="text-gold" />
                                </span>
                                <div>
                                    <span className="label-eyebrow">Disponibilità</span>
                                    <h2 className="font-serif text-xl text-ink-50 mt-0.5">Calendario aggiornato</h2>
                                </div>
                            </div>
                            <div className="mt-6">
                                <AvailabilityCalendar propertyId={property.id} />
                            </div>
                        </div>
                    ) : null;
                    const contactBlockContent = (
                        <aside data-testid="property-contact-card" className="w-full">
                            <div className="relative border border-white/10 p-5 sm:p-6 overflow-hidden">
                                <span className="label-eyebrow">Indirizzo & Contatti</span>

                                <div className={`mt-4 space-y-3 text-sm ${locked ? "blur-locked" : ""}`}>
                                    <div className="flex items-start gap-3 text-ink-100">
                                        <MapPin size={16} className="text-gold mt-0.5" />
                                        <div>
                                            <div>{property.address || "Via Roma 12"}</div>
                                            <div className="text-ink-400">{property.zip_code || "00100"} {property.city}</div>
                                        </div>
                                    </div>
                                    {property.owner_phone && (
                                        <div data-testid="property-contact-phone" className="flex items-center gap-3 text-ink-100">
                                            <Phone size={16} className="text-gold" /> {property.owner_phone}
                                        </div>
                                    )}
                                    <div className="flex items-start gap-3 text-ink-100 min-w-0">
                                        <Mail size={16} className="text-gold shrink-0 mt-0.5" /> <span data-testid="property-contact-email" className="min-w-0 break-words [overflow-wrap:anywhere]">{property.owner_email || "contatto@email.it"}</span>
                                    </div>
                                    <div className="text-ink-400 pt-3 border-t border-white/5">
                                        Pubblicato da: <Link to={`/agenzie/${property.owner_id}`} data-testid="agency-link" className="text-ink-100 hover:text-gold inline-flex items-center gap-1.5">
                                            <Building2 size={12} /> {property.owner_name}
                                        </Link>
                                    </div>
                                </div>

                                {locked && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm p-8 text-center">
                                        {property.chat_unlocked ? (
                                            <>
                                                <div data-testid="chat-unlocked-state" className="w-12 h-12 border border-green-500/40 bg-green-900/30 flex items-center justify-center mb-3">
                                                    <MessageCircle size={20} className="text-green-300" />
                                                </div>
                                                <div className="font-serif text-xl text-ink-50 mb-1">Chat sbloccata</div>
                                                <p className="text-[11px] tracking-wider uppercase text-green-300 mb-4">Dedicata a questo annuncio</p>
                                                <Link
                                                    to={`/chat?to=${property.owner_id}&property=${property.id}&title=${encodeURIComponent(property.title)}`}
                                                    data-testid="open-dedicated-chat-cta"
                                                    className="px-6 py-3 bg-gold text-black text-sm font-medium hover:bg-gold-soft transition-colors flex items-center gap-2 mb-5"
                                                >
                                                    <MessageCircle size={14} /> Apri chat con il proprietario
                                                </Link>
                                                {property.can_request_contacts && (
                                                    <div className="w-full pt-4 border-t border-white/10">
                                                        <div className="flex items-center gap-2 mb-2 justify-center">
                                                            <Lock size={12} className="text-ink-400" />
                                                            <span className="text-xs text-ink-400">Vuoi anche email e telefono?</span>
                                                        </div>
                                                        <RequestConsentButton propertyId={property.id} level="contacts" />
                                                    </div>
                                                )}
                                            </>
                                        ) : property.can_request_contacts ? (
                                            <>
                                                <Lock size={28} className="text-gold mb-4" />
                                                <div className="font-serif text-xl text-ink-50 mb-2">Contatti riservati</div>
                                                <p className="text-sm text-ink-400 mb-6">Email e telefono del proprietario sono privati. Richiedi il suo consenso esplicito per sbloccarli.</p>
                                                <RequestConsentButton propertyId={property.id} level="contacts" />
                                            </>
                                        ) : property.can_request_chat ? (
                                            <>
                                                <Lock size={28} className="text-gold mb-4" />
                                                <div className="font-serif text-xl text-ink-50 mb-2">Contatti riservati</div>
                                                <p className="text-sm text-ink-400 mb-6">Per tutelare la privacy, devi prima ottenere il consenso del proprietario per iniziare la conversazione.</p>
                                                <RequestConsentButton propertyId={property.id} level="chat" />
                                            </>
                                        ) : null}
                                    </div>
                                )}
                                {!locked && property.contacts_unlocked && (
                                    <div className="mt-4 text-xs text-green-300 border border-green-700/30 bg-green-900/20 p-2 inline-flex items-center gap-2">
                                        <Lock size={12} /> Contatti sbloccati con consenso del proprietario
                                    </div>
                                )}
                                {!shortStay && (
                                    <button
                                        data-testid="open-book-visit"
                                        onClick={() => setShowBookVisit(true)}
                                        className="mt-5 w-full cta-gold py-3.5 text-sm flex items-center justify-center gap-2"
                                    >
                                        <CalendarIcon size={14} /> Prenota una visita
                                    </button>
                                )}
                            </div>
                            <div className="mt-6">
                                <ContactForm
                                    propertyId={property.id}
                                    agencyId={property.owner_id}
                                    locked={property.locked}
                                    title={property.owner_role === "agenzia" ? "Contatta l'agenzia" : "Contatta il privato"}
                                />
                            </div>
                        </aside>
                    );

                    if (shortStay) {
                        // Mobile: contenitore unico con grid-cols-1, ogni blocco riposizionato via `order-N` nella
                        // sequenza richiesta (titolo, prezzo, calendario, descrizione, contatti, resto).
                        // Desktop (lg:): i due wrapper tornano blocchi normali ciascuno nella propria colonna
                        // (lg:col-start-1 / lg:col-start-2), i figli si impilano nel loro ordine naturale del DOM
                        // esattamente come nel layout standard, senza alcuna dipendenza dall'auto-placement della griglia.
                        return (
                            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-x-10 items-start">
                                <div className="contents lg:block lg:col-start-1">
                                    <div className="order-1 lg:order-none">{titleBlockContent}</div>
                                    <div className="order-4 lg:order-none mt-8 lg:mt-10">{descBlockContent}</div>
                                    {featBlockContent && <div className="order-6 lg:order-none mt-12">{featBlockContent}</div>}
                                    {featureTagsBlockContent && <div className="order-7 lg:order-none mt-12">{featureTagsBlockContent}</div>}
                                    {planBlockContent && <div className="order-8 lg:order-none mt-12">{planBlockContent}</div>}
                                </div>
                                <div className="contents lg:block lg:col-start-2">
                                    <div className="order-2 lg:order-none mt-8 lg:mt-0">{priceBlockContent}</div>
                                    {availBlockContent && <div className="order-3 lg:order-none mt-6 lg:mt-8">{availBlockContent}</div>}
                                    <div className="order-5 lg:order-none mt-8">{contactBlockContent}</div>
                                </div>
                            </div>
                        );
                    }
                    // Mobile: stessa tecnica dei contratti short-stay — titolo/caratteristiche, poi
                    // prezzo/pulsanti, poi il resto del contenuto nel suo ordine naturale.
                    // Desktop (lg:): invariato, due colonne reali come nel layout originale.
                    return (
                        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-x-10 items-start">
                            <div className="contents lg:block lg:col-start-1">
                                <div className="order-1 lg:order-none">{titleBlockContent}</div>
                                <div className="order-3 lg:order-none mt-8 lg:mt-10">{descBlockContent}</div>
                                {featBlockContent && <div className="order-4 lg:order-none mt-12">{featBlockContent}</div>}
                                {featureTagsBlockContent && <div className="order-5 lg:order-none mt-12">{featureTagsBlockContent}</div>}
                                {planBlockContent && <div className="order-6 lg:order-none mt-12">{planBlockContent}</div>}
                            </div>
                            <div className="contents lg:block lg:col-start-2">
                                <div className="order-2 lg:order-none mt-8 lg:mt-0">{priceBlockContent}</div>
                                <div className="order-7 lg:order-none mt-8">{contactBlockContent}</div>
                            </div>
                        </div>
                    );
                })()}

                {showBookVisit && (
                    <div data-testid="book-visit-modal" className="property-modal-backdrop fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowBookVisit(false)}>
                        <div className="bg-white border border-slate-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
                            <BookVisit propertyId={property.id} propertyTitle={property.title} onClose={() => setShowBookVisit(false)} />
                        </div>
                    </div>
                )}

                {/* MAPPA */}
                <div className="mt-20">
                    <span className="label-eyebrow">Posizione</span>
                    <h2 className="font-serif text-2xl text-ink-50 mt-3 mb-6">Sulla mappa</h2>
                    <PropertyMap lat={property.latitude} lng={property.longitude} city={property.city} locked={property.locked} />
                </div>
            </div>
            <ShareModal propertyId={property.id} open={showShare} onClose={() => setShowShare(false)} />

            {showGalleryModal && (
                <div data-testid="property-gallery-modal" className="property-modal-backdrop fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" onClick={() => setShowGalleryModal(false)}>
                    <div className="property-gallery-modal-panel relative w-full max-w-6xl max-h-[94vh] overflow-hidden bg-white rounded-xl shadow-2xl p-4 sm:p-6" onClick={(event) => event.stopPropagation()}>
                        <div className="flex items-center justify-between gap-4 mb-4">
                            <div data-testid="property-gallery-modal-count" className="font-semibold text-slate-900">{activePhoto + 1}/{photos.length}</div>
                            <button data-testid="property-gallery-modal-close" type="button" onClick={() => setShowGalleryModal(false)} aria-label="Chiudi galleria" className="w-10 h-10 grid place-items-center rounded-full border border-slate-300 text-slate-700 hover:bg-slate-100"><XIcon size={19} /></button>
                        </div>
                        <div className="relative h-[52vh] sm:h-[62vh] bg-slate-100 rounded-lg overflow-hidden">
                            <img data-testid="property-gallery-modal-image" src={photos[activePhoto]} alt={`${property.title} foto ${activePhoto + 1}`} className="w-full h-full object-contain" />
                            {photos.length > 1 && <>
                                <button data-testid="property-gallery-modal-previous" type="button" onClick={() => goToPhoto(-1)} aria-label="Foto precedente" className="property-gallery-control absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-black/60 text-white"><ChevronLeft size={24} /></button>
                                <button data-testid="property-gallery-modal-next" type="button" onClick={() => goToPhoto(1)} aria-label="Foto successiva" className="property-gallery-control absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-black/60 text-white"><ChevronRight size={24} /></button>
                            </>}
                        </div>
                        <div data-testid="property-gallery-modal-thumbnails" className="flex gap-2 overflow-x-auto pt-4 pb-1">
                            {photos.map((photo, index) => (
                                <button key={index} data-testid={`property-gallery-modal-thumb-${index}`} type="button" onClick={() => setActivePhoto(index)} aria-label={`Mostra foto ${index + 1}`} className={`relative w-24 h-16 sm:w-28 sm:h-20 shrink-0 overflow-hidden rounded-md border-2 ${activePhoto === index ? "border-gold" : "border-transparent"}`}>
                                    <img src={photo} alt={`${property.title} foto ${index + 1}`} className="w-full h-full object-cover" />
                                    {photoLabel(index) && <span className="property-gallery-control absolute inset-x-1 bottom-1 bg-black/65 text-white text-[9px] px-1 py-0.5 rounded truncate">{photoLabel(index)}</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Video modal — YouTube/Vimeo embed */}
            {showVideoModal && videoEmbedUrl && (
                <div
                    data-testid="video-modal"
                    className="property-modal-backdrop fixed inset-0 z-[90] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
                    onClick={() => setShowVideoModal(false)}
                >
                    <div className="relative w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
                        <button
                            data-testid="video-modal-close"
                            type="button"
                            onClick={() => setShowVideoModal(false)}
                            aria-label="Chiudi video"
                            className="absolute -top-12 right-0 md:-right-2 md:-top-12 text-slate-800 hover:text-gold flex items-center gap-1.5 text-sm transition-colors"
                        >
                            <XIcon size={20} /> <span className="hidden md:inline">Chiudi</span>
                        </button>
                        <iframe
                            src={videoEmbedUrl}
                            title="Video annuncio"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full border-0 shadow-2xl ring-1 ring-gold/30"
                        />
                    </div>
                </div>
            )}

            {/* Virtual Tour modal — fullscreen overlay */}
            {showTourModal && tourEmbedUrl && (
                <div data-testid="virtual-tour-modal" className="fixed inset-0 z-[90] bg-white flex flex-col">
                    <div className="flex items-center justify-between px-4 md:px-6 py-3 bg-white border-b border-slate-200 shrink-0">
                        <div className="flex items-center gap-2 text-slate-900">
                            <View size={16} className="text-gold" />
                            <span className="font-serif text-sm md:text-base">Virtual Tour · {property.title}</span>
                        </div>
                        <button
                            data-testid="virtual-tour-modal-close"
                            type="button"
                            onClick={() => setShowTourModal(false)}
                            aria-label="Chiudi virtual tour"
                            className="inline-flex items-center gap-1.5 text-slate-700 hover:text-gold transition-colors text-sm"
                        >
                            <XIcon size={18} /> Chiudi
                        </button>
                    </div>
                    <iframe
                        src={tourEmbedUrl}
                        title="Virtual Tour 3D"
                        allow="fullscreen; xr-spatial-tracking; gyroscope; accelerometer; vr"
                        allowFullScreen
                        className="flex-1 w-full border-0"
                    />
                </div>
            )}

            {/* Photo Tour modal — auto-slideshow Ken Burns dalle foto già caricate */}
            {showPhotoTour && photoTourReady && (
                <PhotoTour photos={photos} title={property.title} onClose={() => setShowPhotoTour(false)} fullscreen />
            )}
        </div>
    );
}


function RequestConsentButton({ propertyId, level }) {
    const [sent, setSent] = useState(false);
    const [chatOpened, setChatOpened] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState("");
    const send = async () => {
        setBusy(true); setError(null);
        try {
            const { data } = await api.post(`/properties/${propertyId}/request-contact`, { property_id: propertyId, message: msg, level });
            if (data?.chat_opened) setChatOpened(true);
            setSent(true);
        } catch (e) {
            setError(e?.response?.data?.detail || "Errore invio richiesta");
        } finally { setBusy(false); }
    };
    if (sent && chatOpened) {
        return (
            <div data-testid="chat-unlocked-box" className="text-xs text-green-300 border border-green-700/40 bg-green-900/20 px-4 py-3 space-y-2">
                <div>✓ Chat sbloccata! La conversazione è stata aperta con il proprietario.</div>
                <a data-testid="go-to-chat-link" href="/chat" className="inline-block px-4 py-2 bg-gold text-black font-medium hover:bg-gold-soft transition-colors">Vai alla chat →</a>
            </div>
        );
    }
    if (sent) {
        return <div data-testid={`consent-sent-${level}`} className="text-xs text-green-300 border border-green-700/40 bg-green-900/20 px-4 py-3">✓ Richiesta inviata. Il proprietario riceverà un&apos;email e potrà accettare con un click.</div>;
    }
    return (
        <div className="w-full">
            <textarea
                data-testid={`consent-msg-${level}`}
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="Messaggio facoltativo per il proprietario (es. 'Sono interessato a una visita...')"
                rows={2}
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-xs mb-2"
            />
            <button
                data-testid={`consent-btn-${level}`}
                onClick={send}
                disabled={busy}
                className="w-full px-6 py-3 bg-gold text-black text-sm font-medium hover:bg-gold-soft transition-colors disabled:opacity-50"
            >
                {busy ? "Invio..." : level === "contacts" ? "Richiedi sblocco contatti" : "Sblocca chat"}
            </button>
            {error && <div className="text-xs text-red-300 mt-2">{error}</div>}
        </div>
    );
}
